import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  LinearProgress,
  Typography,
  Avatar,
  Box,
  Alert,
  IconButton,
  Grid,
  TextField,
  InputAdornment,
} from '@mui/material';
import { Close, Devices, Router, Cable, Search, Clear, Wifi } from '@mui/icons-material';
import equipmentService from '../../services/equipmentService';
import EquipmentForm from '../forms/EquipmentForm';
import Swal from 'sweetalert2';

interface Equipment {
  id: number;
  modeloId: number;
  numero_serie: string;
  mac_address?: string;
  gpon_sn?: string;
  modalidade: 'proprio' | 'comodato';
  status_uso: 'livre' | 'em_uso';
  end_estoque?: string;
  modelo?: {
    id: number;
    titulo: string;
    tipo: 'ONU' | 'ONT' | 'Roteador' | 'Switch';
    marca?: {
      id: number;
      titulo: string;
    };
  };
}

interface TrocarEquipamentoModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  equipment: any | null; // Equipamento atual a ser substituído
  contractId: number;
}

const TrocarEquipamentoModal: React.FC<TrocarEquipamentoModalProps> = ({
  open,
  onClose,
  onSuccess,
  equipment: currentEquipment,
  contractId,
}) => {
  const [availableEquipments, setAvailableEquipments] = useState<Equipment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [equipmentFormOpen, setEquipmentFormOpen] = useState(false);
  const [equipmentSearch, setEquipmentSearch] = useState('');

  const isONU = useMemo(() => {
    if (!currentEquipment?.modelo?.tipo) return false;
    const tipo = currentEquipment.modelo.tipo.toLowerCase();
    return tipo === 'onu' || tipo === 'ont';
  }, [currentEquipment]);

  useEffect(() => {
    if (open && currentEquipment) {
      setAvailableEquipments([]);
      setError(null);
      setEquipmentSearch('');
      loadAvailableEquipments();
    }
  }, [open, currentEquipment]);

  const loadAvailableEquipments = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await equipmentService.getEquipments({
        status_uso: 'livre',
        limit: 100,
      });

      let equipmentsData: Equipment[] = [];
      if (response && response.success && response.data && (response.data as any).equipments && Array.isArray((response.data as any).equipments)) {
        equipmentsData = (response.data as any).equipments;
      } else if (response && response.success && response.data && Array.isArray(response.data)) {
        equipmentsData = response.data;
      } else if (response && (response as any).equipments && Array.isArray((response as any).equipments)) {
        equipmentsData = (response as any).equipments;
      } else if (response && Array.isArray(response)) {
        equipmentsData = response;
      } else if (response && response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
        const dataKeys = Object.keys(response.data);
        for (const key of dataKeys) {
          const value = (response.data as any)[key];
          if (Array.isArray(value)) {
            equipmentsData = value;
            break;
          }
        }
      } else {
        setError('Formato de dados inesperado da API de equipamentos');
        return;
      }

      // Filtrar conforme o tipo do equipamento atual
      if (isONU) {
        // Se o equipamento atual for ONU/ONT, mostrar apenas equipamentos ONU/ONT livres
        const filtered = equipmentsData.filter((item: Equipment) => {
          const tipo = item.modelo?.tipo?.toLowerCase();
          return tipo === 'onu' || tipo === 'ont';
        });
        setAvailableEquipments(filtered);
      } else {
        // Se for outro equipamento (Roteador/Switch), mostrar apenas Roteador/Switch livres
        const filtered = equipmentsData.filter((item: Equipment) => {
          const tipo = item.modelo?.tipo?.toLowerCase();
          return tipo === 'roteador' || tipo === 'switch';
        });
        setAvailableEquipments(filtered);
      }
    } catch (err: any) {
      console.error('Erro ao carregar equipamentos disponíveis:', err);
      setError(`Erro ao carregar equipamentos: ${err.message || err}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEquipmentFormSuccess = () => {
    setEquipmentFormOpen(false);
    loadAvailableEquipments();
  };

  const getEquipmentTypeIcon = (tipo?: string) => {
    switch (tipo?.toLowerCase()) {
      case 'onu':
      case 'ont':
        return <Wifi sx={{ fontSize: 16 }} />;
      case 'roteador':
        return <Router sx={{ fontSize: 16 }} />;
      case 'switch':
        return <Cable sx={{ fontSize: 16 }} />;
      default:
        return <Devices sx={{ fontSize: 16 }} />;
    }
  };

  const filteredEquipments = useMemo(() => {
    return availableEquipments.filter((eq) => {
      if (!equipmentSearch.trim()) return true;
      const search = equipmentSearch.toLowerCase();
      const modeloTitulo = eq.modelo?.titulo?.toLowerCase() || '';
      const marcaTitulo = eq.modelo?.marca?.titulo?.toLowerCase() || '';
      const numeroSerie = eq.numero_serie?.toLowerCase() || '';
      const macAddress = eq.mac_address?.toLowerCase() || '';
      const gponSn = eq.gpon_sn?.toLowerCase() || '';
      const endEstoque = eq.end_estoque?.toLowerCase() || '';

      return (
        modeloTitulo.includes(search) ||
        marcaTitulo.includes(search) ||
        numeroSerie.includes(search) ||
        macAddress.includes(search) ||
        gponSn.includes(search) ||
        endEstoque.includes(search)
      );
    });
  }, [availableEquipments, equipmentSearch]);

  const handleSelectNewEquipment = async (newEquipment: Equipment) => {
    if (!currentEquipment) return;

    const confirmResult = await Swal.fire({
      title: 'Confirmar Troca de Equipamento',
      html: `
        <div style="text-align: left; font-size: 14px;">
          <p><strong>Equipamento Atual:</strong> ${currentEquipment.modelo?.titulo || 'N/A'} (S/N: ${currentEquipment.numero_serie})</p>
          <p><strong>Novo Equipamento:</strong> ${newEquipment.modelo?.titulo || 'N/A'} (S/N: ${newEquipment.numero_serie})</p>
          ${isONU ? `
            <div style="margin-top: 10px; padding: 8px; background: #e3f2fd; border-radius: 4px; color: #0d47a1;">
              <p style="margin: 0;">ℹ️ <strong>ONU:</strong> O novo equipamento manterá a mesma <strong>CTO / Porta (${currentEquipment.caixa?.name || currentEquipment.caixa_name || 'N/A'} - Porta ${currentEquipment.caixa_port_number || 'N/A'})</strong> e o mesmo <strong>Circuito</strong>.</p>
            </div>
          ` : ''}
          <br />
          <p>Deseja confirmar a substituição?</p>
        </div>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sim, substituir',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#1976d2',
      customClass: {
        container: 'swal-zindex',
      },
    });

    if (!confirmResult.isConfirmed) return;

    try {
      setLoading(true);
      setError(null);

      // 1. Preparar dados para o novo equipamento
      const updateDataNovo: any = {
        contrato_id: contractId,
        contrato_servico_id: currentEquipment.contrato_servico_id || currentEquipment.servico_id || null,
      };

      if (isONU) {
        // Manter na mesma CTO, mesma porta e mesmo circuito
        updateDataNovo.caixa_id = currentEquipment.caixa_id;
        updateDataNovo.caixa_port_number = currentEquipment.caixa_port_number;
        if (currentEquipment.circuito_id) {
          updateDataNovo.circuito_id = currentEquipment.circuito_id;
        }
      }

      // 2. Vincular o novo equipamento ao contrato (e CTO/circuito se for ONU)
      await equipmentService.updateEquipment(newEquipment.id, updateDataNovo);

      // 3. Devolver o equipamento antigo
      await equipmentService.devolverEquipamento(currentEquipment.id);

      await Swal.fire({
        title: 'Sucesso!',
        text: 'Equipamento substituído com sucesso!',
        icon: 'success',
        timer: 3000,
        timerProgressBar: true,
        customClass: {
          container: 'swal-zindex',
        },
      });

      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Erro ao trocar equipamento:', err);
      const msg = err.response?.data?.message || err.response?.data?.error || err.message || 'Erro ao realizar a troca do equipamento';
      setError(msg);
      await Swal.fire({
        title: 'Erro!',
        text: msg,
        icon: 'error',
        customClass: {
          container: 'swal-zindex',
        },
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ p: 1.5 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
            Trocar Equipamento
          </Typography>
          <IconButton onClick={onClose} size="small" sx={{ p: 0.5 }}>
            <Close sx={{ fontSize: '1rem' }} />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 1.5 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 1.5, fontSize: '0.8rem' }}>
            {error}
          </Alert>
        )}
        {loading && <LinearProgress sx={{ mb: 1.5 }} />}

        {currentEquipment && (
          <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: '#f8f9fa' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, color: '#333' }}>
              Equipamento que está sendo substituído:
            </Typography>
            <Grid container spacing={1} sx={{ fontSize: '0.85rem' }}>
              <Grid item xs={12} sm={4}>
                <strong>Modelo:</strong> {currentEquipment.modelo?.titulo || 'N/A'} ({currentEquipment.modelo?.tipo || 'N/A'})
              </Grid>
              <Grid item xs={12} sm={4}>
                <strong>Série:</strong> {currentEquipment.numero_serie || 'N/A'}
              </Grid>
              <Grid item xs={12} sm={4}>
                <strong>Modalidade:</strong> {currentEquipment.modalidade === 'proprio' ? 'Próprio' : 'Comodato'}
              </Grid>
              {isONU && (
                <>
                  <Grid item xs={12} sm={6}>
                    <strong>CTO / Porta:</strong> {currentEquipment.caixa?.name || currentEquipment.caixa_name || 'N/A'} - Porta {currentEquipment.caixa_port_number || 'N/A'}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <strong>Circuito:</strong> {currentEquipment.circuito?.identificacao || (currentEquipment.circuito_id ? `ID ${currentEquipment.circuito_id}` : 'Sem circuito')}
                  </Grid>
                </>
              )}
            </Grid>
          </Paper>
        )}

        <Typography variant="body2" sx={{ fontSize: '0.85rem', mb: 1.5, color: 'text.secondary' }}>
          {isONU
            ? 'Selecione abaixo a nova ONU/ONT para substituir. Ela manterá a mesma CTO, porta e circuito.'
            : 'Selecione abaixo o novo Roteador/Switch para substituir o equipamento atual.'}
        </Typography>

        <Grid container spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth
              size="small"
              placeholder={isONU ? "Pesquisar por modelo, série, GPON, MAC..." : "Pesquisar por modelo, série, MAC..."}
              value={equipmentSearch}
              onChange={(e) => setEquipmentSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: '1rem' }} />
                  </InputAdornment>
                ),
                endAdornment: equipmentSearch && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setEquipmentSearch('')}>
                      <Clear sx={{ fontSize: '0.9rem' }} />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button
              fullWidth
              variant="outlined"
              color="primary"
              onClick={() => setEquipmentFormOpen(true)}
              disabled={loading}
              size="small"
              sx={{ fontSize: '0.8rem', py: 0.75, height: 40, whiteSpace: 'nowrap' }}
            >
              Cadastrar Equipamento
            </Button>
          </Grid>
        </Grid>

        <EquipmentForm
          open={equipmentFormOpen}
          onClose={() => setEquipmentFormOpen(false)}
          onSuccess={handleEquipmentFormSuccess}
        />

        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 380 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ py: 0.75, fontSize: '0.8rem', fontWeight: 700, bgcolor: 'grey.50' }}>Tipo</TableCell>
                <TableCell sx={{ py: 0.75, fontSize: '0.8rem', fontWeight: 700, bgcolor: 'grey.50' }}>{isONU ? 'Marca/Modelo' : 'Modelo'}</TableCell>
                <TableCell sx={{ py: 0.75, fontSize: '0.8rem', fontWeight: 700, bgcolor: 'grey.50' }}>Série</TableCell>
                <TableCell sx={{ py: 0.75, fontSize: '0.8rem', fontWeight: 700, bgcolor: 'grey.50' }}>{isONU ? 'GPON / MAC' : 'MAC Address'}</TableCell>
                <TableCell sx={{ py: 0.75, fontSize: '0.8rem', fontWeight: 700, bgcolor: 'grey.50' }}>Modalidade</TableCell>
                <TableCell sx={{ py: 0.75, fontSize: '0.8rem', fontWeight: 700, bgcolor: 'grey.50' }}>Localização</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredEquipments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                      {availableEquipments.length === 0
                        ? `Nenhum equipamento ${isONU ? 'ONU/ONT' : 'Roteador/Switch'} disponível em estoque`
                        : 'Nenhum equipamento encontrado para a pesquisa'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredEquipments.map((eq) => (
                  <TableRow
                    key={eq.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleSelectNewEquipment(eq)}
                  >
                    <TableCell sx={{ py: 0.5, px: 1.5 }}>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Avatar sx={{ width: 24, height: 24, bgcolor: 'primary.main' }}>
                          {getEquipmentTypeIcon(eq.modelo?.tipo)}
                        </Avatar>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                          {eq.modelo?.tipo || 'N/A'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 0.5, px: 1.5 }}>
                      <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.8rem' }}>
                        {eq.modelo?.titulo || 'N/A'}
                      </Typography>
                      {eq.modelo?.marca?.titulo && (
                        <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>
                          {eq.modelo.marca.titulo}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ py: 0.5, px: 1.5 }}>
                      <Typography variant="body2" fontFamily="monospace" sx={{ fontSize: '0.8rem' }}>
                        {eq.numero_serie}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 0.5, px: 1.5 }}>
                      {isONU ? (
                        <>
                          <Typography variant="body2" fontFamily="monospace" sx={{ fontSize: '0.8rem' }}>
                            {eq.gpon_sn || '-'}
                          </Typography>
                          {eq.mac_address && (
                            <Typography variant="caption" fontFamily="monospace" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>
                              {eq.mac_address}
                            </Typography>
                          )}
                        </>
                      ) : (
                        <Typography variant="body2" fontFamily="monospace" sx={{ fontSize: '0.8rem' }}>
                          {eq.mac_address || '-'}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ py: 0.5, px: 1.5 }}>
                      <Chip
                        label={eq.modalidade === 'proprio' ? 'Próprio' : 'Comodato'}
                        color={eq.modalidade === 'proprio' ? 'primary' : 'secondary'}
                        size="small"
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 0.5, px: 1.5 }}>
                      <Typography variant="body2" fontFamily="monospace" sx={{ fontSize: '0.8rem' }}>
                        {eq.end_estoque || '-'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions sx={{ p: 1.5 }}>
        <Button onClick={onClose} disabled={loading} size="small" sx={{ fontSize: '0.8rem' }}>
          Cancelar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TrocarEquipamentoModal;
